declare const Packeta: {
	Widget: {
		pick: (
			apiKey: string,
			callback: (point: PacketaPoint | PacketaHdResult | null) => void,
			options: Record<string, unknown>,
			inElement?: HTMLElement | null,
		) => void;
	};
};

interface PacketaPoint {
	id: number;
	name: string;
	city: string;
	street: string;
	zip: string;
	country: string;
	carrierId: string | null;
	carrierPickupPointId: string | null;
}

interface PacketaHdResult {
	address: {
		street: string;
		houseNumber: string;
		city: string;
		postcode: string;
		county: string;
		latitude: number;
		longitude: number;
	} | null;
}

class Order {
	private readonly termCheckboxNames: string[] = [
		'[data-terms="1"]',
		'[data-terms="2"]',
	];

	private readonly copyFromData = 'data-copy-address-from';
	private readonly copyToData = "data-copy-address-to";

	private readonly termCheckboxes: { [key: string]: HTMLInputElement } = {};
	private readonly termCheckboxState: { [key: string]: boolean } = {};

	private readonly copyTargets: { [key: string]: string } = {};
	private packetaLibraryPromise: Promise<void> | null = null;

	public constructor() {
		this.init();
	}

	private init(): void {
		for (const termCheckboxName of this.termCheckboxNames) {
			const el = document.querySelectorAll(termCheckboxName) as NodeListOf<HTMLInputElement>;

			if (el === undefined || el.length !== 1) {
				continue;
			}

			this.termCheckboxes[termCheckboxName] = el[0];
			this.termCheckboxState[termCheckboxName] = this.termCheckboxes[termCheckboxName].checked;
		}

		for (const termCheckboxName in this.termCheckboxes) {
			const element = this.termCheckboxes[termCheckboxName];

			element.addEventListener('change', () => {
				console.log(termCheckboxName);
				if (this.termCheckboxState[termCheckboxName] === element.checked) {
					return;
				}

				this.termCheckboxState[termCheckboxName] = element.checked;

				for (const innerTermCheckboxName in this.termCheckboxes) {
					if (
						this.termCheckboxState[innerTermCheckboxName] === element.checked
						|| innerTermCheckboxName === termCheckboxName
					) {
						continue;
					}

					const innerTermCheckbox = this.termCheckboxes[innerTermCheckboxName];
					this.termCheckboxState[innerTermCheckboxName] = element.checked;
					innerTermCheckbox.checked = element.checked;
				}
			});
		}

		const copyFrom = document.querySelectorAll(`[${this.copyFromData}]`);

		copyFrom.forEach((sourceElement) => {
			const source = sourceElement.getAttribute(this.copyFromData);

			this.copyTargets[source] = sourceElement.getAttribute(this.copyToData);

			sourceElement.addEventListener('click', this.copyAddress(source).bind(this));
			console.log(sourceElement);
		});

		console.log(this.copyTargets);
		this.initPacketa();
	}

	private copyAddress(source: string): (Event) => any {
		return _ => {
			const destination = this.copyTargets[source];

			console.log(source, destination);

			this.copySourceToDestination('base', destination);
			this.copySourceToDestination(source, destination);
		};
	}

	private copySourceToDestination(source: string, destination: string): void {
		const sourceLength = source.length;
		const sources = document.querySelectorAll(`[name^="${source}["]`);
		const sourcesArr = Array.from(sources);
		console.log(...sourcesArr);

		sources.forEach((sourceElement) => {
			const name = sourceElement.getAttribute('name').substring(sourceLength);

			const destinationElement = document.querySelector(`[name^="${destination}${name}"]`) as HTMLInputElement;

			const inputSourceElement = sourceElement as HTMLInputElement;
			const value = inputSourceElement.value;

			console.log(name, destinationElement, value);

			if (destinationElement !== null && value !== null && value !== '') {
				destinationElement.value = value;
			}
		});
	}

	private initPacketa(): void {
		const pickupWidgetContainer = document.querySelector('#packeta-pickup-widget-container') as HTMLElement | null;
		const hdWidgetContainer = document.querySelector('#packeta-hd-widget-container') as HTMLElement | null;

		if (pickupWidgetContainer === null && hdWidgetContainer === null) {
			return;
		}

		const apiKeySource = pickupWidgetContainer ?? hdWidgetContainer;
		const apiKey = apiKeySource?.attributes.getNamedItem('data-packeta-api-key')?.value as string | undefined;

		if (apiKey === undefined || apiKey === '') {
			return;
		}

		const hdCarrierId = apiKeySource?.attributes.getNamedItem('data-packeta-hd-carrier-id')?.value as string | undefined;
		const deliveryMethodInputs = document.querySelectorAll('input[name="deliveryMethod"]') as NodeListOf<HTMLInputElement>;

		// Pickup summary elements
		const pickupSummary = document.querySelector('[data-packeta-summary="pickup-selected-point"]') as HTMLElement | null;
		const pickupSummaryName = pickupSummary?.querySelector('[data-packeta-summary="pickup-name"]') as HTMLElement | null;
		const pickupSummaryAddress = pickupSummary?.querySelector('[data-packeta-summary="pickup-address"]') as HTMLElement | null;
		const pickupChangeButton = pickupSummary?.querySelector('[data-packeta-summary="pickup-change"]') as HTMLElement | null;

		// HD summary elements
		const hdSummary = document.querySelector('[data-packeta-summary="hd-selected-point"]') as HTMLElement | null;
		const hdSummaryName = hdSummary?.querySelector('[data-packeta-summary="hd-name"]') as HTMLElement | null;
		const hdSummaryAddress = hdSummary?.querySelector('[data-packeta-summary="hd-address"]') as HTMLElement | null;
		const hdChangeButton = hdSummary?.querySelector('[data-packeta-summary="hd-change"]') as HTMLElement | null;

		const updateSummary = (nameEl: HTMLElement | null, addressEl: HTMLElement | null, pointName: string, pointAddress: string): void => {
			if (nameEl !== null) {
				nameEl.textContent = pointName;
			}

			if (addressEl !== null) {
				addressEl.textContent = pointAddress;
			}
		};

		const buildPickupAddressFromFields = (): { pointName: string; pointAddress: string } => {
			const pointName = this.getFieldValueByPath('packetaPoint[pickup][name]');
			const street = this.getFieldValueByPath('packetaPoint[pickup][address][street]');
			const city = this.getFieldValueByPath('packetaPoint[pickup][address][city]');
			const zip = this.getFieldValueByPath('packetaPoint[pickup][address][zip]');
			const cityWithZip = [zip, city].filter((value) => value !== '').join(' ');
			const pointAddress = [street, cityWithZip].filter((value) => value !== '').join(', ');

			return { pointName, pointAddress };
		};

		const buildHdAddressFromFields = (): { pointName: string; pointAddress: string } => {
			const pointName = this.getFieldValueByPath('packetaPoint[homeDelivery][name]');
			const street = this.getFieldValueByPath('packetaPoint[homeDelivery][address][street]');
			const city = this.getFieldValueByPath('packetaPoint[homeDelivery][address][city]');
			const zip = this.getFieldValueByPath('packetaPoint[homeDelivery][address][zip]');
			const cityWithZip = [zip, city].filter((value) => value !== '').join(' ');
			const pointAddress = [street, cityWithZip].filter((value) => value !== '').join(', ');

			return { pointName, pointAddress };
		};

		const openPickupWidget = (): void => {
			if (pickupWidgetContainer === null) {
				return;
			}

			Packeta.Widget.pick(
				apiKey,
				(point: PacketaPoint | null) => {
					if (point === null) {
						return;
					}

					this.setFieldByPath('packetaPoint[pickup][pointId]', String(point.id));
					this.setFieldByPath('packetaPoint[pickup][name]', point.name);
					this.setFieldByPath('packetaPoint[pickup][carrierId]', point.carrierId ?? '');
					this.setFieldByPath('packetaPoint[pickup][carrierPickupPointId]', point.carrierPickupPointId ?? '');
					this.setFieldByPath('packetaPoint[pickup][address][city]', point.city);
					this.setFieldByPath('packetaPoint[pickup][address][street]', point.street);
					this.setFieldByPath('packetaPoint[pickup][address][zip]', point.zip);
					updateSummary(pickupSummaryName, pickupSummaryAddress, point.name, [point.street, `${point.zip} ${point.city}`].filter((value) => value !== '').join(', '));
					pickupWidgetContainer.hidden = true;

					if (pickupSummary !== null) {
						pickupSummary.hidden = false;
					}
				},
				{
					country: 'cz',
					language: 'cs',
					packetConsignment: 'true',
					view: 'inlineFull',
				},
				pickupWidgetContainer,
			);
		};

		const openHdWidget = (): void => {
			if (hdWidgetContainer === null) {
				return;
			}

			Packeta.Widget.pick(
				apiKey,
				(result: PacketaHdResult | null) => {
					if (result === null || !('address' in result) || result.address === null) {
						return;
					}

					const address = result.address;
					const streetFull = [address.street, address.houseNumber].filter((v) => v !== '').join(' ');

					this.setFieldByPath('packetaPoint[homeDelivery][name]', 'Doručení na adresu');
					this.setFieldByPath('packetaPoint[homeDelivery][carrierId]', hdCarrierId ?? '');
					this.setFieldByPath('packetaPoint[homeDelivery][address][city]', address.city);
					this.setFieldByPath('packetaPoint[homeDelivery][address][street]', streetFull);
					this.setFieldByPath('packetaPoint[homeDelivery][address][zip]', address.postcode);
					updateSummary(hdSummaryName, hdSummaryAddress, 'Doručení na adresu', [streetFull, `${address.postcode} ${address.city}`].filter((value) => value !== '').join(', '));
					hdWidgetContainer.hidden = true;

					if (hdSummary !== null) {
						hdSummary.hidden = false;
					}
				},
				{
					layout: 'hd',
					carrierId: hdCarrierId ?? '',
					country: 'cz',
					language: 'cs',
				},
				hdWidgetContainer,
			);
		};

		const hideAllWidgets = (): void => {
			if (pickupWidgetContainer !== null) {
				pickupWidgetContainer.hidden = true;
			}

			if (pickupSummary !== null) {
				pickupSummary.hidden = true;
			}

			if (hdWidgetContainer !== null) {
				hdWidgetContainer.hidden = true;
			}

			if (hdSummary !== null) {
				hdSummary.hidden = true;
			}
		};

		const updateDeliveryMethodVisibility = (): void => {
			const selectedDeliveryMethod = Array.from(deliveryMethodInputs).find((input) => input.checked)?.value;

			if (selectedDeliveryMethod === 'packeta_pickup') {
				// Hide HD widget and summary
				if (hdWidgetContainer !== null) {
					hdWidgetContainer.hidden = true;
				}

				if (hdSummary !== null) {
					hdSummary.hidden = true;
				}

			// Point already selected (from session, current selection, or method switch) — show summary
			if (this.hasSelectedPickupPoint()) {
					const { pointName, pointAddress } = buildPickupAddressFromFields();
					updateSummary(pickupSummaryName, pickupSummaryAddress, pointName, pointAddress);

				if (pickupWidgetContainer !== null) {
					pickupWidgetContainer.hidden = true;
				}

				if (pickupSummary !== null) {
					pickupSummary.hidden = false;
				}

				return;
				}

				// No point selected — open widget

			if (pickupSummary !== null) {
				pickupSummary.hidden = true;
			}

			if (pickupWidgetContainer !== null) {
					pickupWidgetContainer.hidden = false;
				}

				void this.loadPacketaLibrary().then(() => {
					openPickupWidget();
				});
				return;
			}

			if (selectedDeliveryMethod === 'packeta_hd') {
				// Hide pickup widget and summary
				if (pickupWidgetContainer !== null) {
					pickupWidgetContainer.hidden = true;
				}

				if (pickupSummary !== null) {
					pickupSummary.hidden = true;
				}

			// Address already selected (from session, current selection, or method switch) — show summary
			if (this.hasSelectedHdAddress()) {
					const { pointName, pointAddress } = buildHdAddressFromFields();
					updateSummary(hdSummaryName, hdSummaryAddress, pointName, pointAddress);

				if (hdWidgetContainer !== null) {
					hdWidgetContainer.hidden = true;
				}

				if (hdSummary !== null) {
					hdSummary.hidden = false;
				}

				return;
				}

				// No address selected — open widget

			if (hdSummary !== null) {
				hdSummary.hidden = true;
			}

			if (hdWidgetContainer !== null) {
					hdWidgetContainer.hidden = false;
				}

				void this.loadPacketaLibrary().then(() => {
					openHdWidget();
				});
				return;
			}

			hideAllWidgets();
		};

		pickupChangeButton?.addEventListener('click', () => {
			if (pickupWidgetContainer !== null) {
				pickupWidgetContainer.hidden = false;
			}

			if (pickupSummary !== null) {
				pickupSummary.hidden = true;
			}

			void this.loadPacketaLibrary().then(() => {
				openPickupWidget();
			});
		});

		hdChangeButton?.addEventListener('click', () => {
			if (hdWidgetContainer !== null) {
				hdWidgetContainer.hidden = false;
			}

			if (hdSummary !== null) {
				hdSummary.hidden = true;
			}

			void this.loadPacketaLibrary().then(() => {
				openHdWidget();
			});
		});

		deliveryMethodInputs.forEach((input) => {
			input.addEventListener('change', () => {
				updateDeliveryMethodVisibility();
			});
		});

		updateDeliveryMethodVisibility();
	}

	private loadPacketaLibrary(): Promise<void> {
		if (this.packetaLibraryPromise !== null) {
			return this.packetaLibraryPromise;
		}

		this.packetaLibraryPromise = new Promise((resolve, reject) => {
			if (typeof Packeta !== 'undefined') {
				resolve();
				return;
			}

			const existingScript = document.querySelector('script[data-packeta-widget="1"]') as HTMLScriptElement | null;

			if (existingScript !== null) {
				if (existingScript.dataset.loaded === '1') {
					resolve();
					return;
				}

				existingScript.addEventListener('load', () => {
					existingScript.dataset.loaded = '1';
					resolve();
				}, { once: true });
				existingScript.addEventListener('error', () => {
					this.packetaLibraryPromise = null;
					reject(new Error('Unable to load Packeta widget library.'));
				}, { once: true });
				return;
			}

			const script = document.createElement('script');
			script.src = 'https://widget.packeta.com/v6/www/js/library.js';
			script.async = true;
			script.dataset.packetaWidget = '1';
			script.addEventListener('load', () => {
				script.dataset.loaded = '1';
				resolve();
			}, { once: true });
			script.addEventListener('error', () => {
				this.packetaLibraryPromise = null;
				reject(new Error('Unable to load Packeta widget library.'));
			}, { once: true });
			document.head.appendChild(script);
		});

		return this.packetaLibraryPromise;
	}

	private hasSelectedPickupPoint(): boolean {
		return this.getFieldValueByPath('packetaPoint[pickup][pointId]') !== '';
	}

	private hasSelectedHdAddress(): boolean {
		return this.getFieldValueByPath('packetaPoint[homeDelivery][address][street]') !== '';
	}

	private setFieldByPath(fieldPath: string, value: string): void {
		const field = document.querySelector(`input[name="${fieldPath}"]`) as HTMLInputElement | null;

		if (field !== null) {
			field.value = value;
		}
	}

	private getFieldValueByPath(fieldPath: string): string {
		const field = document.querySelector(`input[name="${fieldPath}"]`) as HTMLInputElement | null;

		return field?.value ?? '';
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const order = new Order();
});
